import { useLocale } from "@/hooks/useLocale";
import { AnimatePresence, motion } from "framer-motion";
import { Link } from "react-router-dom";

import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/utils/cn";
import { isActive } from "@/utils/helper";
import { preloadByUrl } from "@/utils/preloadByUrl";
import type { INavItem } from "./sidebar.types";

interface INavItemProps {
  item: INavItem;
  isCollapsed: boolean;
  currentPath: string;
  onLinkClick: () => void;
  /**
   * Overrides the path-prefix match. For menus whose items differ by query
   * string only (the settings sidebar's `?vTab=` pages), which a pathname
   * comparison cannot tell apart.
   */
  active?: boolean;
}

export const NavItem = ({
  item,
  isCollapsed,
  currentPath,
  onLinkClick,
  active,
}: INavItemProps) => {
  const { byDir } = useLocale();
  const isActiveLink = active ?? isActive(currentPath, item.url);

  const linkEl = (
    <Link
      to={item.url}
      onClick={onLinkClick}
      onMouseEnter={() => preloadByUrl(item.url)}
      onFocus={() => preloadByUrl(item.url)}
      aria-current={isActiveLink ? "page" : undefined}
      // The icon rail drops the label; the tooltip is not an accessible name.
      aria-label={isCollapsed ? item.name : undefined}
      className={cn(
        "group relative flex items-center rounded-xs overflow-hidden",
        "outline-hidden focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-1",
        isCollapsed
          ? "justify-center w-8 h-8 lg:w-10 lg:h-10 mx-auto"
          : "gap-2 lg:gap-3 ps-1 pe-2 lg:ps-1.5 lg:pe-3 py-1 w-full",
        isActiveLink
          ? "text-white"
          : "text-foreground hover:bg-primary/10 hover:text-primary transition-colors duration-150",
      )}
    >
      {/* Sliding active background */}
      {isActiveLink && (
        <motion.div
          layoutId="sidebar-nav-active-bg"
          className="absolute inset-0 bg-primary"
          transition={{ type: "spring", stiffness: 380, damping: 30 }}
        />
      )}

      {/* Active start-border indicator */}
      <AnimatePresence>
        {!isCollapsed && isActiveLink && (
          <motion.span
            layoutId="nav-active-indicator"
            className="absolute start-0 inset-y-0 my-auto w-[3px] h-4 bg-white rounded-full z-10"
            transition={{ type: "spring", stiffness: 500, damping: 35 }}
          />
        )}
      </AnimatePresence>

      {/* Icon with scale pop */}
      <motion.span
        animate={{ scale: isActiveLink ? 1.08 : 1 }}
        transition={{ type: "spring", stiffness: 400, damping: 25 }}
        className={cn(
          "relative z-10 shrink-0 flex items-center justify-center rounded-xs p-1 w-7 h-7 lg:w-8 lg:h-8",
          isActiveLink
            ? "bg-white/25 text-white"
            : "bg-primary/10 text-primary",
        )}
      >
        {item.icon}
      </motion.span>

      {/* Label */}
      <AnimatePresence initial={false}>
        {!isCollapsed && (
          <motion.span
            key="label"
            initial={{ opacity: 0, x: byDir(-6, 6) }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: byDir(-6, 6) }}
            transition={{ duration: 0.16, ease: [0.4, 0, 0.2, 1], delay: 0.08 }}
            className={cn(
              "relative z-10 text-xs lg:text-sm font-semibold truncate flex-1 min-w-0",
              isActiveLink ? "text-white" : "text-foreground",
            )}
          >
            {item.name}
          </motion.span>
        )}
      </AnimatePresence>
    </Link>
  );

  if (isCollapsed) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>{linkEl}</TooltipTrigger>
        <TooltipContent
          side={byDir("right", "left")}
          className="text-xs font-medium"
        >
          {item.name}
        </TooltipContent>
      </Tooltip>
    );
  }

  return linkEl;
};
